import {
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  input,
  Input,
  Output,
  QueryList,
  signal,
  Signal,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  SeFeModalButton,
  SeFeModalComponent,
  SeFeModalFooterComponent,
} from 'se-fe-modal';
import {AnalyticsService} from 'src/app/common/service';
import {
  CartItem,
  CartItemSpecificationInterface,
  Image,
  isDisplayableImageSrc,
  resolveFirstDisplayableImage,
  SpecificationMap,
  Variant,
  YsoProduct,
} from '../../../../data/model';
import {YsoProductFormComponent} from '../yso-product-form/yso-product-form.component';
import {YsoBundleItemComponent} from '../yso-bundle-item/yso-bundle-item.component';
import type {CartItemInterface} from '../../../../data/model';
import {buildBundleInstanceId} from '../../../../common/shopping-cart/bundle-instance-id';

@Component({
  selector: 'app-yso-product',
  templateUrl: './yso-product.component.html',
  styleUrls: ['./yso-product.component.scss'],
  host: {class: 'app-yso-product'},
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class YsoProductComponent {
  @Input() organizationName: string;

  @Output() addToCartClick = new EventEmitter<CartItem>();
  @Output() addClareBundleToCart = new EventEmitter<{
    items: CartItemInterface[];
    bundleDisplayName: string;
  }>();
  @ViewChildren(YsoBundleItemComponent)
  bundleItems: QueryList<YsoBundleItemComponent>;
  @ViewChild('modalFooter') modalFooter: SeFeModalFooterComponent;
  @ViewChildren('productForm') productForms: QueryList<YsoProductFormComponent>;
  @ViewChild('productForm') ysoProductForm: YsoProductFormComponent;
  @ViewChild('productModal') productModal: SeFeModalComponent;

  item = input.required<YsoProduct>();
  variantMap = input<Record<string, Variant[]>>();

  variants: Signal<Variant[]> = computed(() => {
    if (!this.variantMap() || !this.item()) return [];

    return this.variantMap()![this.item().id] ?? [];
  });

  readonly carouselContentConfig = computed(() => {
    const images = (this.item()?.images ?? []).filter(img =>
      isDisplayableImageSrc(img?.src),
    );
    const total = images.length;

    return total > 1
      ? {
          metadata: {total, startIndex: 0, endIndex: total - 1},
          slides: [...images],
        }
      : null;
  });

  itemDetailFooterButtons = computed<SeFeModalButton[]>(() => {
    const isAllFormsReady = this.allFormsReady;
    return [
      {
        label: 'Cancel',
        action: (): void => {
          this.productModal.close();
          return;
        },
      },
      {
        primary: true,
        label: 'Add to cart',
        disabled: !isAllFormsReady,
        action: async (): Promise<void> => {
          if (isAllFormsReady) {
            if (this.item().type === 'Bundle') {
              this.addBundleToCart();
            } else {
              this.addToCart();
            }
          }
          return;
        },
      },
    ];
  });

  specificationMapValue = computed(() => {
    if (this.item()?.specifications && this.item().specifications!.length > 0) {
      return {
        ...this._specificationMap(),
        [this.item().id]: this.item().specifications!,
      };
    }
    return this._specificationMap();
  });

  expandedProducts: Record<string, boolean> = {};
  formReadyStatus = signal<boolean[]>([]);
  validSignal = signal<boolean>(false);
  ready = false;

  readonly carouselConfig = {
    fixedSlides: 1,
    width: 300,
    height: 300,
    controls: 'below' as const,
  };

  private _specificationMap = signal<SpecificationMap>({});

  constructor(
    private changeDetector: ChangeDetectorRef,
    private analyticsService: AnalyticsService,
  ) {}

  get allFormsReady(): boolean {
    if (this.item().type !== 'Bundle') {
      return this.validSignal();
    }
    return (
      this.formReadyStatus().length > 0 &&
      this.formReadyStatus().every(ready => ready)
    );
  }

  get image(): Image {
    return resolveFirstDisplayableImage(this.item()?.images);
  }

  get specificationMap(): SpecificationMap {
    return this._specificationMap();
  }

  @Input() set specificationMap(value: SpecificationMap) {
    if (!value) return;

    this._specificationMap.set(value);
    this.ready = true;
    this.changeDetector.detectChanges();
  }

  addToCart(): void {
    if (this.item().type === 'Bundle') {
      this.addBundleToCart();
      return;
    }

    if (!this.ysoProductForm.valid) {
      this.modalFooter?.cancelSpinner();
      this.ysoProductForm.setOptionsDirtyFlagTrue();
      return;
    }
    const cartItem = new CartItem({
      ...this.ysoProductForm.value,
      itemVariationId: this.ysoProductForm.selectedVariant()?.id,
      name: this.item().name,
      unitPrice: this.item().price,
    });
    this.addToCartClick.emit(cartItem);
    this.productModal?.close();
  }

  openModal(): void {
    // Initialize validSignal to false so it can be updated by onFormReady
    this.validSignal.set(false);
    this.changeDetector.detectChanges();

    this.productModal.open({
      ...this.item(),
      title: 'Choose Product Options',
      focus: (): void => {},
    });
    this.analyticsService.viewProductDetails(this.item());
  }

  resetFormControls(): void {
    if (this.item().type === 'Bundle') {
      this.bundleItems.forEach(item => item.productForm?.resetFormControls());
      this.expandedProducts = {};
    } else {
      this.ysoProductForm?.resetFormControls();
    }
    // Reset signals when modal closes so it initializes correctly on reopen
    this.validSignal.set(false);
    this.formReadyStatus.set([]);
  }

  onFormReady(isReady: boolean): void {
    this.validSignal.set(isReady);
    this.changeDetector.detectChanges();
    this.changeDetector.markForCheck();
  }

  onBundleFormReady(index: number, isReady: boolean): void {
    this.formReadyStatus.update(status => {
      const currentProductsCount = this.item()?.products?.length || 0;
      let newStatus = [...status];
      if (newStatus.length < currentProductsCount) {
        newStatus = new Array(currentProductsCount).fill(false);
        status.forEach((s, i) => (newStatus[i] = s));
      }
      newStatus[index] = isReady;
      return newStatus;
    });
    this.changeDetector.detectChanges();
    this.changeDetector.markForCheck();
  }

  toggleDetails(productId: string): void {
    this.expandedProducts[productId] = !this.expandedProducts[productId];
  }

  isExpanded(productId: string): boolean {
    return !!this.expandedProducts[productId];
  }

  getVariantsForProduct(productId: string): Variant[] {
    const product =
      this.item().id === productId
        ? this.item()
        : this.item().products?.find(p => p.id === productId);

    if (product?.variants && product.variants.length > 0) {
      return product.variants;
    }
    return this.variantMap()?.[productId] ?? [];
  }

  private addBundleToCart(): void {
    const items = this.bundleItems.toArray();
    let allValid = true;

    items.forEach(item => {
      if (item.productForm && !item.productForm.valid) {
        allValid = false;
        item.productForm.setOptionsDirtyFlagTrue();
      }
    });

    if (!allValid) {
      this.modalFooter?.cancelSpinner();
      return;
    }

    const bundleIdResolved =
      this.item().bundleId ??
      (Number.isFinite(Number(this.item().id)) ? Number(this.item().id) : NaN);

    if (!Number.isFinite(bundleIdResolved)) {
      this.modalFooter?.cancelSpinner();
      return;
    }

    const cartItems: CartItemInterface[] = [];

    for (const item of items) {
      if (!item.productForm) continue;

      const line = item.productForm.value;
      const itemVariationId = this.resolveBundleItemVariationId(item);

      if (itemVariationId === undefined) {
        allValid = false;
        item.productForm.setOptionsDirtyFlagTrue();
        break;
      }

      const nestedSpecs = item.productForm.productForm?.value?.specs;
      const directSpecs = item.productForm.value?.specs;
      const specifications = nestedSpecs || directSpecs;

      // We include specs in the object for generating a deterministic bundle ID
      // but they might not be part of the final CartItemInterface for the API
      cartItems.push({
        productId: item.product.id,
        name: item.product.name,
        templateKey: item.product.templateKey,
        itemVariationId,
        quantity: line.quantity ?? 1,
        unitPrice: item.productForm.currentPrice,
        bundleId: bundleIdResolved,
        references: line.references ?? undefined,
        fulfilledBy: item.product.fulfillment,
        bundle_name: this.item().name,
        bundled_product_id: item.product.id,
        specs: specifications as unknown as CartItemSpecificationInterface[],
      });
    }

    if (!allValid) {
      this.modalFooter?.cancelSpinner();
      return;
    }

    // Generate a deterministic bundle instance id based on products, variations and specs
    const bundleInstanceId = buildBundleInstanceId(
      bundleIdResolved,
      cartItems.map(i => ({
        productId: i.productId,
        itemVariationId: i.itemVariationId,
        specs: i.specs ?? [],
      })),
    );

    cartItems.forEach(i => {
      i.bundle_instance_id = bundleInstanceId;
      i.specs = undefined;
    });

    this.addClareBundleToCart.emit({
      items: cartItems,
      bundleDisplayName: this.item().name,
    });
    this.productModal?.close();
  }

  /**
   * Resolves variation id from form value, selected variant, or a single implicit variant.
   */
  private resolveBundleItemVariationId(
    item: YsoBundleItemComponent,
  ): number | undefined {
    return (
      this.toFiniteNumber(item.productForm?.value?.itemVariationId) ??
      this.toFiniteNumber(item.productForm?.selectedVariant?.()?.id) ??
      this.implicitSingleBundleVariantVariationId(item)
    );
  }

  /**
   * When exactly one variant exists for the bundle line, use its id without explicit selection.
   */
  private implicitSingleBundleVariantVariationId(
    item: YsoBundleItemComponent,
  ): number | undefined {
    const bundleItemVariants =
      item.bundleItemVariants?.length > 0
        ? item.bundleItemVariants
        : this.getVariantsForProduct(item.product.id);
    if (bundleItemVariants.length !== 1) {
      return undefined;
    }
    return this.toFiniteNumber(bundleItemVariants[0]?.id);
  }

  private toFiniteNumber(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}
